"use client"

import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Edit2, Calendar, Mail, MessageSquare, Target, Users, BarChart2, Clock, Send, User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CampaignService from '@/api/campaign.service';
import { format } from 'date-fns';

interface CampaignDetailsProps {
  publicId: string;
}

export default function Campaign({ publicId }: CampaignDetailsProps) {
  const [campaign, setCampaign] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchCampaign = async () => {
      setIsLoading(true);
      try {
        const response = await CampaignService.getCampaign(publicId);
        
        if (response.data.result === 'success') {
          setCampaign(response.data.campaign);
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to load campaign details",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching campaign:", error);
        toast({
          title: "Error",
          description: "Failed to load campaign details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    if (publicId) {
      fetchCampaign();
    }
  }, [publicId, toast]);

  const handleSendCampaign = async () => {
    if (!campaign) return;
    
    try {
      const response = await CampaignService.sendCampaignNow(campaign.id, { status: 'sent' });
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Campaign sent successfully",
        });
        
        // Update local state
        setCampaign(prev => ({ ...prev, status: 'sent' }));
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to send campaign",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error sending campaign:", error);
      toast({
        title: "Error",
        description: "An error occurred while sending the campaign",
        variant: "destructive",
      });
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel?.toLowerCase()) {
      case 'email':
        return <Mail className="w-5 h-5 text-blue-500" />;
      case 'sms':
        return <MessageSquare className="w-5 h-5 text-green-500" />;
      case 'whatsapp':
        return <MessageSquare className="w-5 h-5 text-green-600" />;
      default:
        return <Mail className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200">Draft</Badge>;
      case 'scheduled':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">Scheduled</Badge>;
      case 'sent':
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Sent</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading campaign details...</p>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <p className="text-muted-foreground">Campaign not found</p>
        <Button onClick={() => window.history.back()} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="container py-8 mx-auto max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => window.history.back()} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">
              {campaign.name}
            </h1>
            <div className="ml-2">
              {getStatusBadge(campaign.status)}
            </div>
          </div>
          
          <div className="flex gap-2">
            {campaign.status === 'draft' && (
              <Button 
                onClick={handleSendCampaign} 
                className="gap-2"
              >
                <Send className="w-4 h-4" />
                Send Now
              </Button>
            )}
            
            {campaign.status === 'draft' && (
              <Button 
                variant="outline"
                className="gap-2 border-primary/20 text-primary hover:bg-primary/10"
              >
                <Edit2 className="w-4 h-4" />
                Edit Campaign
              </Button>
            )}
          </div>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Campaign Overview */}
          <Card className="md:col-span-1 shadow-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Campaign Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  {getChannelIcon(campaign.channel)}
                  <div>
                    <p className="text-sm font-medium">Channel</p>
                    <p className="text-sm text-muted-foreground">
                      {campaign.channel ? campaign.channel.charAt(0).toUpperCase() + campaign.channel.slice(1) : 'N/A'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Recipients</p>
                    <p className="text-sm text-muted-foreground">{campaign.recipientCount || 0} recipients</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Created By</p>
                    <p className="text-sm text-muted-foreground">{campaign.createdByName || 'Unknown'}</p>
                  </div>
                </div>
                
                {campaign.status === 'scheduled' && campaign.scheduledAt && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Scheduled For</p>
                      <p className="text-sm text-muted-foreground">
                        {campaign.scheduledAt && format(new Date(campaign.scheduledAt), "MMM d, yyyy 'at' HH:mm")}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Created On</p>
                    <p className="text-sm text-muted-foreground">
                      {campaign.dateCreated && format(new Date(campaign.dateCreated), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              {/* Status */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Status</p>
                <div className="flex items-center gap-2">
                  {getStatusBadge(campaign.status)}
                  <span className="text-sm text-muted-foreground">
                    {campaign.status === 'draft' && 'This campaign is in draft mode and ready to be sent.'}
                    {campaign.status === 'scheduled' && 'This campaign is scheduled to be sent.'}
                    {campaign.status === 'sent' && 'This campaign has been sent to all recipients.'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Right Column - Campaign Content */}
          <div className="md:col-span-2 space-y-6">
            <Tabs defaultValue="content" className="w-full">
              <TabsList className="grid grid-cols-2 w-full md:w-[400px]">
                <TabsTrigger value="content">Campaign Content</TabsTrigger>
                <TabsTrigger value="stats">
                  {campaign.status === 'sent' ? 'Performance Stats' : 'Preview'}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="content" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardContent className="pt-6">
                    <div className="space-y-6">
                      {campaign.channel === 'email' && (
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Subject</p>
                          <p className="font-medium">{campaign.subject || 'No subject'}</p>
                        </div>
                      )}
                      
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Message</p>
                        <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border">
                          <div 
                            className="prose dark:prose-invert max-w-none" 
                            dangerouslySetInnerHTML={{ __html: campaign.message || 'No message content' }}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="stats" className="mt-4">
                {campaign.status === 'sent' ? (
                  <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="p-4 border rounded-lg bg-primary/5">
                          <p className="text-sm text-muted-foreground">Sent</p>
                          <p className="text-2xl font-bold">{campaign.recipientCount || 0}</p>
                        </div>
                        <div className="p-4 border rounded-lg bg-primary/5">
                          <p className="text-sm text-muted-foreground">Opens</p>
                          <p className="text-2xl font-bold">{campaign.stats?.opens || '0'}</p>
                          <p className="text-xs text-muted-foreground">
                            {campaign.stats?.openRate ? `${campaign.stats.openRate}%` : '0%'}
                          </p>
                        </div>
                        <div className="p-4 border rounded-lg bg-primary/5">
                          <p className="text-sm text-muted-foreground">Clicks</p>
                          <p className="text-2xl font-bold">{campaign.stats?.clicks || '0'}</p>
                          <p className="text-xs text-muted-foreground">
                            {campaign.stats?.clickRate ? `${campaign.stats.clickRate}%` : '0%'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                    <CardContent className="pt-6">
                      <div className="text-center py-8">
                        <BarChart2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">No Stats Available</h3>
                        <p className="text-muted-foreground mb-6">
                          Statistics will be available after the campaign has been sent.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
} 